'use client';

import { Button, Drawer as DrawerSparky, Icon, Logo, TextLink } from '@petsmart-ui/sparky';

import { LegacyIcon, LegacyIconName, useCart, useUserSession } from '@libs/common-next';
import { logoutUser, NAVIGATION_URLS } from '@libs/core';
import type { Entry } from 'contentful';
import { useEffect, useState } from 'react';
import { LegacyMinicart } from '../LegacyMinicart';
import type { LegacyHeaderDrawerProps } from './types';

import { useCategoryStack } from '../../hooks';
import { ComponentResolver } from '../../utils';
import { LegacyDrawerMenuItem } from '../LegacyHeaderDrawerItem';

import './LegacyHeaderDrawer.scss';

export default function LegacyHeaderDrawer({ mainMenu, secondaryNavigation }: LegacyHeaderDrawerProps) {
    const [isActive, setIsActive] = useState(false);
    const { storageUserSession, user } = useUserSession();
    const { firstName, isRegistered } = storageUserSession ?? {};
    const [displayLoggedIn, setDisplayLoggedIn] = useState<boolean | undefined>(undefined);
    const { clearCart } = useCart();

    const { currentCategories, categoryStack, handleCategoryClick, handleBackClick } = useCategoryStack(
        mainMenu?.fields?.category as Entry[]
    );

    useEffect(() => {
        setDisplayLoggedIn(!!user);
    }, [firstName, isRegistered, user]);

    return (
        <>
            <Button
                data-testid="menu-button"
                variant="bare"
                text={isActive ? 'Close' : 'Menu'}
                iconPosition="before"
                iconName={isActive ? 'x' : 'menu'}
                size="lg"
                onClick={() => setIsActive(true)}
                className="header-drawer__button"
                hideText
            />

            <DrawerSparky
                showBackdrop={false}
                className="legacy-drawer"
                isActive={isActive}
                onClose={() => setIsActive(false)}
                data-testid="legacy-header-drawer"
            >
                <DrawerSparky.Header titleAfter>
                    <div className="header-drawer__account">
                        <a href={NAVIGATION_URLS.legacy.account.base} title="Rewards & Account">
                            <LegacyIcon aria-hidden="true" name={LegacyIconName.PERSON} size={20} />
                        </a>
                    </div>
                    <Logo />
                    <div className="header-drawer__cart">
                        <a href={NAVIGATION_URLS.legacy.storeLocator.base} title="Locate Stores">
                            <LegacyIcon aria-hidden="true" name={LegacyIconName.PIN} size={24} />
                        </a>
                        <LegacyMinicart />
                    </div>
                </DrawerSparky.Header>
                {currentCategories?.categories && (
                    <DrawerSparky.Body className="drawer-body">
                        {categoryStack.length > 1 ? (
                            <div className="header-drawer header-drawer--top">
                                <button
                                    data-testid="back-button"
                                    onClick={handleBackClick}
                                    className="category__button--back category__button"
                                    type="button"
                                >
                                    <Icon size="lg" name="chevron-left" />
                                    <span>{currentCategories.displayText}</span>
                                </button>
                            </div>
                        ) : null}
                        <ul
                            data-testid="drawer-body"
                            className={`menu-category menu-category--lvl${categoryStack.length}`}
                        >
                            {currentCategories.categories
                                // NOTE: Remove duplicate categories to avoid phantom menu items on back button click
                                // TODO: should be remove if we can get the category stack to work properly with unique IDs
                                .filter(
                                    (category, index, self) => index === self.findIndex(c => c.sysId === category.sysId)
                                )
                                .map(category => (
                                    <LegacyDrawerMenuItem
                                        key={category.sysId}
                                        categoryStack={categoryStack}
                                        category={category}
                                        handleCategoryClick={handleCategoryClick}
                                        closeMenu={() => setIsActive(false)}
                                    />
                                ))}
                        </ul>
                        <ComponentResolver
                            className={`bottom-nav-links bottom-nav-links--lvl${categoryStack.length}`}
                            entry={secondaryNavigation}
                            isHeader={false}
                        />
                        <ul
                            className={`mobile-top-menu hidden-lg bottom-nav-links bottom-nav-links--lvl${categoryStack.length}`}
                        >
                            <li className="bottom-login-link">
                                {displayLoggedIn !== undefined && displayLoggedIn ? (
                                    <p data-testid="logout" className="logout-anchor">
                                        {`Not ${user?.gn}?`}
                                        <TextLink
                                            onClick={async () => await logoutUser('/account', clearCart)}
                                            className="logout-btn"
                                        >
                                            Sign Out
                                        </TextLink>
                                    </p>
                                ) : (
                                    <a
                                        data-testid="login"
                                        href={NAVIGATION_URLS.legacy.account.base}
                                        className="login-anchor"
                                    >
                                        Create Account
                                        <span className="login-btn">Sign In</span>
                                    </a>
                                )}
                            </li>
                        </ul>
                    </DrawerSparky.Body>
                )}
                <DrawerSparky.Footer className="mobile-nav-footer">
                    <a className="customer-care-number-wrapper" href="tel:18888399638">
                        <span className="circled-phone">
                            <Icon name="contact-outline" />
                        </span>
                        <span className="customer-care-number">1-888-839-9638</span>
                    </a>
                </DrawerSparky.Footer>
            </DrawerSparky>
        </>
    );
}
